"use client"

import { useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import { LineChart, Line, ResponsiveContainer, YAxis, Tooltip as RechartsTooltip } from "recharts"
import { fetchWithTimeout } from "@/lib/fetch-utils"
import { getTodayInUserTimezone } from "@/lib/date-utils"
import { formatPrice } from "@/lib/format-utils"
import { TrendingUp, TrendingDown } from "lucide-react"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { ChartTooltipContainer, ChartTooltipDate } from "@/components/charts/tooltips/chart-tooltip"

interface PriceDataPoint {
  date: string
  price: number
}

type SparklinePeriod = "24h" | "7d" | "1mo"

interface TokenSparklineProps {
  tokenAddress: string
  currentPrice?: number // Latest oracle price to use for the most recent data point
  period?: SparklinePeriod
  className?: string
}

async function fetchTokenPriceHistory(tokenAddress: string, period: SparklinePeriod = "1mo"): Promise<PriceDataPoint[]> {
  // Note: We request 2 days for 24h period to handle timezone differences between
  // server (UTC) and user's local time. The server generates dates using CURRENT_DATE (UTC),
  // but the client filters using user's local timezone. If user is behind UTC,
  // server's "today" becomes "tomorrow" for user and gets filtered out, leaving only 1 point.
  // With 2 days, we ensure at least 2 data points remain after timezone filtering.
  const daysMap = {
    "24h": "2",
    "7d": "7",
    "1mo": "30",
  }

  const params = new URLSearchParams({
    token: tokenAddress,
    days: daysMap[period],
  })

  const response = await fetchWithTimeout(`/api/token-price-history?${params}`)
  if (!response.ok) {
    return []
  }

  const data = await response.json()
  return data.history || []
}

// Custom tooltip component (shared chart tooltip styling)
function SparklineTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: PriceDataPoint }> }) {
  if (!active || !payload?.length) {
    return null
  }

  const data = payload[0].payload
  return (
    <ChartTooltipContainer>
      <ChartTooltipDate date={data.date} />
      <p className="text-sm font-medium text-white">{formatPrice(data.price)}</p>
    </ChartTooltipContainer>
  )
}

// Calculate price change percentage for given period
function calculatePriceChange(priceHistory: PriceDataPoint[]): { percentage: number; trend: "up" | "down" | "unchanged" } {
  if (!priceHistory?.length || priceHistory.length < 2) {
    return { percentage: 0, trend: "unchanged" }
  }
  const startPrice = priceHistory[0].price
  const endPrice = priceHistory[priceHistory.length - 1].price

  if (startPrice === 0) {
    return { percentage: 0, trend: "unchanged" }
  }

  const percentage = ((endPrice - startPrice) / startPrice) * 100

  // Consider changes less than 0.01% as unchanged
  if (Math.abs(percentage) < 0.01) {
    return { percentage: 0, trend: "unchanged" }
  }

  return {
    percentage,
    trend: percentage > 0 ? "up" : "down"
  }
}

// Inline sparkline component
export function TokenSparkline({
  tokenAddress,
  currentPrice,
  period = "1mo",
  className = "",
}: TokenSparklineProps) {
  const shouldRenderSparkline = period !== "24h"

  const { data: priceHistory } = useQuery({
    queryKey: ["token-sparkline", tokenAddress, period],
    queryFn: () => fetchTokenPriceHistory(tokenAddress, period),
    enabled: shouldRenderSparkline,
    staleTime: 5 * 60 * 1000, // 5 minutes - match oracle refresh rate
    refetchInterval: false,
  })

  // Add current oracle price if provided, filtering out future dates
  const chartData = useMemo(() => {
    if (!priceHistory?.length) return []

    const today = getTodayInUserTimezone()

    // Filter out any dates that are "in the future" from user's timezone perspective
    // This handles the case where server (UTC) is ahead of the user's timezone
    const filteredData = priceHistory.filter(d => d.date <= today)

    if (!filteredData.length) return []
    if (currentPrice === undefined) return filteredData

    const data = [...filteredData]

    // Use SDK price for today's data point
    const todayIndex = data.findIndex(d => d.date === today)
    if (todayIndex !== -1) {
      data[todayIndex] = {
        ...data[todayIndex],
        price: currentPrice,
      }
    } else {
      data.push({
        date: today,
        price: currentPrice,
      })
    }

    return data
  }, [priceHistory, currentPrice])

  // For this component we compare first vs last for trend.
  const { trend } = useMemo(() => calculatePriceChange(chartData), [chartData])

  if (!shouldRenderSparkline || !chartData?.length) {
    return null
  }

  const strokeColor = trend === "up" ? "var(--positive)" : trend === "down" ? "var(--negative)" : "rgba(255, 255, 255, 0.25)"

  return (
    <div className={`h-6 w-full max-w-48 ${className}`}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{ top: 2, right: 2, bottom: 2, left: 2 }}
        >
          <YAxis domain={["dataMin", "dataMax"]} hide />
          <RechartsTooltip
            content={<SparklineTooltip />}
            cursor={{ stroke: "rgba(255, 255, 255, 0.2)", strokeWidth: 1 }}
            wrapperStyle={{ zIndex: 50 }}
            position={{ y: -50 }}
          />
          <Line
            type="monotone"
            dataKey="price"
            stroke={strokeColor}
            strokeWidth={1}
            dot={false}
            activeDot={{ r: 2, fill: strokeColor }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

// Price change indicator component
export function Token30dChange({
  tokenAddress,
  currentPrice,
  period = "1mo",
  showPrice = false,
  onToggle,
}: {
  tokenAddress: string
  currentPrice?: number // Latest oracle price to use for the most recent data point
  period?: SparklinePeriod
  showPrice?: boolean // Toggle to show current price instead of percentage
  onToggle?: () => void // Callback when tapped to toggle display mode
}) {
  const { data: priceHistory } = useQuery({
    queryKey: ["token-sparkline", tokenAddress, period],
    queryFn: () => fetchTokenPriceHistory(tokenAddress, period),
    staleTime: 5 * 60 * 1000, // 5 minutes - match oracle refresh rate
    refetchInterval: false,
  })

  // Add current oracle price if provided, filtering out future dates
  const chartData = useMemo(() => {
    if (!priceHistory?.length) return []

    const today = getTodayInUserTimezone()

    // Filter out any dates that are "in the future" from user's timezone perspective
    // This handles the case where server (UTC) is ahead of the user's timezone
    const filteredData = priceHistory.filter(d => d.date <= today)

    if (!filteredData.length) return []
    if (currentPrice === undefined) return filteredData

    const data = [...filteredData]

    // Use SDK price for today's data point
    const todayIndex = data.findIndex(d => d.date === today)
    if (todayIndex !== -1) {
      data[todayIndex] = {
        ...data[todayIndex],
        price: currentPrice,
      }
    } else {
      data.push({
        date: today,
        price: currentPrice,
      })
    }

    return data
  }, [priceHistory, currentPrice])

  // For 24h, compare only the last 2 data points (yesterday vs today)
  // For longer periods, compare first vs last to show full period change
  const { percentage, trend } = useMemo(() => {
    if (period === "24h" && chartData.length > 2) {
      return calculatePriceChange(chartData.slice(-2))
    }
    return calculatePriceChange(chartData)
  }, [chartData, period])

  if (!chartData?.length) {
    return null
  }

  const colorClass = trend === "up" ? "text-positive" : trend === "down" ? "text-negative" : "text-white/25"
  const Icon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : null

  // Label based on period
  const periodLabel = period === "24h" ? "24h" : period === "7d" ? "7d" : "30d"

  // Get the display price (use currentPrice if available, otherwise last price from history)
  const displayPrice = currentPrice ?? chartData[chartData.length - 1]?.price

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={`flex items-center gap-0.5 text-xs ${colorClass} ${onToggle ? "cursor-pointer active:opacity-70" : "cursor-default"}`}
          onClick={onToggle}
        >
          {showPrice ? (
            <span>{formatPrice(displayPrice)}</span>
          ) : (
            <>
              {Icon && <Icon className="h-3 w-3" />}
              <span>{Math.abs(percentage).toFixed(1)}%</span>
            </>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent>{showPrice ? "Current price" : `${periodLabel} price change`}</TooltipContent>
    </Tooltip>
  )
}

// Legacy export for backwards compatibility
export const TokenSparklineBg = TokenSparkline
